export const text = (value, fallback = "Not reported") =>
  typeof value === "string" && value.trim() ? value.trim() : fallback;
export const humanize = (value) =>
  String(value).toLowerCase() === "ok"
    ? "OK"
    : text(value)
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
export function timestamp(value) {
  if (!value) return null;
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : null;
}
export function dateLabel(value, compact = false) {
  const time = timestamp(value);
  if (time === null) return "Not reported";
  return new Date(time).toLocaleString(
    undefined,
    compact
      ? { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }
      : {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          timeZoneName: "short",
        },
  );
}
export function resultTone(value) {
  const status = String(value ?? "").toLowerCase();
  if (
    [
      "ok",
      "complete",
      "completed",
      "success",
      "succeeded",
      "healthy",
      "delivered",
      "sent",
    ].includes(status)
  )
    return "success";
  if (["error", "failed", "failure", "missed", "bounced"].includes(status))
    return "danger";
  return "neutral";
}
const pad = (number) => String(number).padStart(2, "0");
// Interpret only exact, simple patterns. Complex cron stays visible without guessing occurrences or timezone.
export function describeSchedule(value) {
  const raw = text(value, "");
  const interval = /^every\s+(\d+(?:\.\d+)?)\s*(s|m|h|d)$/i.exec(raw);
  if (interval && Number(interval[1]) > 0) {
    const amount = Number(interval[1]);
    const unit = { s: "second", m: "minute", h: "hour", d: "day" }[
      interval[2].toLowerCase()
    ];
    return {
      kind: "Interval",
      label: `Every ${amount} ${unit}${amount === 1 ? "" : "s"}`,
      detail:
        "Repeats at the reported interval. The next run comes from the scheduler.",
      beats: ["Run", `+${amount}${interval[2]}`, "Repeat"],
      raw,
      frequent:
        amount * { s: 1, m: 60, h: 3600, d: 86400 }[interval[2].toLowerCase()] <
        604800,
    };
  }
  const fields = raw.split(/\s+/);
  if (fields.length === 5 && fields.every(field => /^[\d*,/\-]+$/.test(field))) {
    const [minute, hour, day, month, weekday] = fields;
    const base = {
      kind: "Cron",
      raw,
      fields,
      label: "Custom schedule",
      detail:
        "The scheduler uses this expression. Exact future occurrences are not inferred here.",
      beats: null,
      frequent: false,
    };
    if (day === "*" && month === "*" && /^[0-7]$/.test(weekday)
      && /^\d{1,2}$/.test(minute) && +minute < 60
      && /^\d{1,2}$/.test(hour) && +hour < 24) {
      const dayName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][+weekday % 7];
      return { ...base, label: `Every ${dayName}`, detail: `Every ${dayName} at ${pad(hour)}:${pad(minute)} in the job’s timezone.`, beats: [dayName, "+1 week", "+2 weeks"] };
    }
    if (day === "*" && month === "*" && weekday === "*") {
      if (hour === "*" && (minute === "0,30" || minute === "*/30"))
        return {
          ...base,
          label: "Every 30 minutes",
          detail: "At minute 00 and minute 30 of every hour.",
          beats: [":00", ":30", ":00"],
          frequent: true,
        };
      if (hour === "*" && /^\d{1,2}$/.test(minute) && +minute < 60)
        return {
          ...base,
          label: "Every hour",
          detail: `At minute ${pad(minute)} of every hour.`,
          beats: [`:${pad(minute)}`, "+1 hour", "+2 hours"],
          frequent: true,
        };
      if (hour === "*" && minute === "*")
        return {
          ...base,
          label: "Every minute",
          detail: "At every minute of every hour.",
          beats: ["Run", "+1 minute", "+2 minutes"],
          frequent: true,
        };
      const step = /^\*\/(\d+)$/.exec(minute);
      if (
        hour === "*" &&
        step &&
        +step[1] > 0 &&
        +step[1] < 60 &&
        60 % +step[1] === 0
      )
        return {
          ...base,
          label: `Every ${+step[1]} minutes`,
          detail: `Every ${+step[1]} minutes, starting at minute 00 each hour.`,
          beats: [":00", `:${pad(step[1])}`, "Repeat"],
          frequent: true,
        };
      if (
        /^\d{1,2}$/.test(minute) &&
        +minute < 60 &&
        /^\d{1,2}$/.test(hour) &&
        +hour < 24
      )
        return {
          ...base,
          label: "Once a day",
          detail: `At ${pad(hour)}:${pad(minute)} in the job’s timezone.`,
          beats: [`${pad(hour)}:${pad(minute)}`, "Next day", "Repeats daily"],
          frequent: true,
        };
    }
    return base;
  }
  return {
    kind: raw ? "Reported schedule" : "Unreported",
    raw,
    label: raw || "Schedule not reported",
    detail: "See the schedule reported by your agent below.",
    beats: null,
    frequent: false,
  };
}
export function jobView(job, index = 0) {
  const name = text(job.name, `Published automation ${index + 1}`);
  const schedule = describeSchedule(job.schedule);
  const category = /blog|content|draft|publish/i.test(name)
    ? "Content"
    : /report|analytics|performance|\bMER\b/i.test(name)
      ? "Reporting"
      : "General";
  const review = /\bweekly\b/i.test(name) && schedule.frequent;
  return { ...job, name, category, cadence: schedule, review };
}
export function changePrompt(job, clarification = false) {
  if (!job)
    return "Help me plan a new automation for this workspace. Clarify the purpose, schedule, timezone and expected output with me before making any changes.";
  return `Please ${clarification ? "clarify the schedule and timezone" : "help me review a change"} for “${job.name}”.\n\nPublished job: ${job.id}\nReported schedule: ${text(job.schedule)}\nJob timezone: ${text(job.timezone)}\n\n${job.review ? "The name says weekly, but the reported cadence is more frequent. Please confirm the intended timing.\n\n" : ""}Explain the proposed change and wait for my approval before modifying the automation.`;
}
